import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CircularProgress, Grid } from '@mui/material'
import { makeStyles, useTheme } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import { Defaults, Text, Utils } from '@pbt/pbt-ui-components'

import type { FilterSectionData } from '~/components/common/filters/MultiSectionFiltersFacetsWidget/FilterSections'
import {
  AppointmentFilterType,
  DEFAULT_CHARGE_SEARCH_FILTERS,
  PatientFilterType,
} from '~/constants/chargeSearchFilters'
import { OrderType } from '~/constants/SOAPStates'
import { fetchMoreForSearch, searchOrders } from '~/store/actions/orders'
import {
  useGetDisabledOrderWeakMap,
  useGetSelectedOrderWithNotes,
} from '~/store/hooks/orders'
import {
  getInventoryCategory,
  getInventoryCategoryIds,
  getSearchCatalogFiltersAndFacetsEnabled,
} from '~/store/reducers/constants'
import {
  getAppliedPatientRestrictions,
  getOrdersIsReceiving,
  getOrdersList,
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
  getOrdersTotalCount,
  getSearchAggregations,
  type PatientRestrictionType,
} from '~/store/reducers/orders'
import { getSoapId } from '~/store/reducers/soap'
import {
  type BaseChargeTabProps,
  DEFAULT_ORDER_LIST_TYPES,
  FulfillmentFacetToBackendValue,
  type OrderFilter,
  OrderListType,
} from '~/types'
import { transformAggregationsToFacets } from '~/utils/aggregationsUtils'
import { hasPriceOrAggregatedPrice } from '~/utils/orderUtils'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'
import { useInfiniteListDynamicHeight } from '~/utils/useInfiniteListDynamicHeight'

import ChargeSearchResultItem from './ChargeSearchResultItem'
import FiltersAndFacetsWidget from './FiltersAndFacetsWidget'

const DEFAULT_HEIGHT = 80
const MIN_HEIGHT = 345

const useStyles = makeStyles(
  (theme) => ({
    hide: {
      display: 'none',
    },
    listContainer: {
      backgroundColor: theme.colors.tableBackground,
      boxShadow:
        '3px 3px 20px 0px rgba(168, 163, 163, 0.5), 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
      borderRadius: '0 0 2px 2px',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 40,
    },
    listContainerLegacySpacing: {
      marginTop: theme.spacing(-2),
      paddingTop: theme.spacing(3),
    },
    mainWrapper: {
      '& > div': {
        flexGrow: 1,
      },
    },
    filterPanel: {
      width: 250,
      borderRight: `1px solid #D8D8D8`,
      backgroundColor: '#FFFFFF',
    },
    contentPanel: {
      flex: 1,
      minWidth: 0,
    },
  }),
  { name: 'ChargeSearchResults' },
)

const FACET_TO_ORDER_TYPE_MAPPING = {
  Labs: OrderType.LAB_TEST,
  Procedures: OrderType.PROCEDURE,
  Inventory: OrderType.INVENTORY,
  Reminders: OrderType.REMINDER,
  Refills: OrderType.REFILL,
  Bundles: OrderType.BUNDLE,
  Prepaid: OrderType.PREPAID,
  'Wellness Plans': OrderType.WPLANS,
} as const

const INVENTORY_CATEGORY_NAME_MAPPING: Record<string, string> = {
  'Food and supplements': 'Food & supplements',
}

export interface ChargeSearchResultsProps extends BaseChargeTabProps {
  appointmentId?: string
  clearSearch?: () => void
  clientId: string
  itemsRefs: React.RefObject<any>[]
  onSearch: (value: string) => void
  onSelect: (
    selectedItem: string | undefined,
    meta?: Record<string, any>,
  ) => void
  orderFilters: OrderFilter[]
  patientId: string
  searchTerm: string
}

const ChargeSearchResults = ({
  appointmentId,
  clearSearch,
  clientId,
  disabled,
  handleEditListItem,
  handleRefill,
  handleSavePrescription,
  itemsRefs,
  logItemStateGetter,
  onSearch,
  openPrescriptionDialog,
  orderFilters,
  patientId,
  searchTerm,
  onSelect,
}: ChargeSearchResultsProps) => {
  const classes = useStyles()
  const theme = useTheme()

  const dispatch = useDispatch()
  const { t } = useTranslation('Search')

  const isReceivingOrders = useSelector(getOrdersIsReceiving)
  const soapId = useSelector(getSoapId)
  const orders = useSelector(getOrdersList)
  const ordersTotalCount = useSelector(getOrdersTotalCount)
  const searchResults = useSelector(getOrdersSearchResults)
  const searchResultsTotalCount = useSelector(getOrdersSearchResultsTotalCount)
  const appliedPatientRestrictions = useSelector(getAppliedPatientRestrictions)
  const isSearchCatalogFiltersAndFacetsEnabled: boolean = useSelector(
    getSearchCatalogFiltersAndFacetsEnabled,
  )
  const InventoryCategory = useSelector(getInventoryCategory)
  const defaultInventoryCategoryIds = useSelector(getInventoryCategoryIds)
  const [selectedListTypes, setSelectedListTypes] = useState<OrderListType[]>(
    DEFAULT_ORDER_LIST_TYPES,
  )
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<
    string | undefined
  >(undefined)

  const [selectedPatientRestrictions, setSelectedPatientRestrictions] =
    useState<PatientRestrictionType[]>(() =>
      DEFAULT_CHARGE_SEARCH_FILTERS.patient
        .filter((filter) => filter.enabled)
        .map((filter) => filter.value as PatientRestrictionType),
    )
  const [selectedInventoryCategoryIds, setSelectedInventoryCategoryIds] =
    useState<string[]>([])

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const [facetSelections, setFacetSelections] = useState<FilterSectionData>({})
  const [filterSelections, setFilterSelections] = useState<FilterSectionData>(
    DEFAULT_CHARGE_SEARCH_FILTERS,
  )

  const searchAggregations = useSelector(getSearchAggregations)
  const baseFacets = transformAggregationsToFacets(searchAggregations)

  const facets = useMemo(() => {
    if (!baseFacets || Object.keys(baseFacets).length === 0) {
      return {}
    }

    const mergedFacets: FilterSectionData = {}

    Object.keys(baseFacets).forEach((sectionKey) => {
      mergedFacets[sectionKey] = baseFacets[sectionKey].map((facet) => {
        const selectedFacet = facetSelections[sectionKey]?.find(
          (selected) => selected.id === facet.id,
        )

        return {
          ...facet,
          enabled: selectedFacet?.enabled || false,
        }
      })
    })

    return mergedFacets
  }, [baseFacets, facetSelections])

  const containerPaddingTop = isSearchCatalogFiltersAndFacetsEnabled
    ? theme.spacing(2)
    : theme.spacing(3)

  const infiniteScrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const filtersRef = useRef<HTMLDivElement>(null)
  const [filtersHeight, setFiltersHeight] = useState<number>(MIN_HEIGHT)

  const items = searchResults.map((result) => result.item)
  const hasSearchedWithoutResults =
    Boolean(searchTerm) && items.length === 0 && !isReceivingOrders
  const hasMinSearchLength = searchTerm.length < Defaults.MIN_SEARCH_LENGTH

  const getSelectedOrderWithNotes = useGetSelectedOrderWithNotes()
  const disabledWeakMap = useGetDisabledOrderWeakMap(
    orders.map(getSelectedOrderWithNotes),
  )

  const dynamicHeight = useInfiniteListDynamicHeight({
    minHeight: MIN_HEIGHT,
    defaultHeight: DEFAULT_HEIGHT,
    hasSearchedWithoutResults,
    infiniteContainerWrapperRef: containerRef,
    isFetchingMore: isReceivingOrders,
    itemsLength: items.length,
  })

  // Filter orderFilters to only include BUNDLE, PROCEDURE, LAB_TEST
  // Should be a backend fix once the old functionality is deprecated. From logs, the other types throws errors
  const filteredOrderFilters = orderFilters.filter((filter) =>
    [OrderType.BUNDLE, OrderType.PROCEDURE, OrderType.LAB_TEST].includes(
      filter.type as OrderType,
    ),
  )
  const hasMoreItems = R.isNil(searchResultsTotalCount)
    ? orders.length < ordersTotalCount
    : searchResults.length < searchResultsTotalCount

  const hasSelectedCategories = selectedCategories.length > 0
  const hasSelectedInventoryCategories = selectedInventoryCategoryIds.length > 0

  const computedFilters = hasSelectedCategories
    ? selectedCategories
    : hasSelectedInventoryCategories
      ? []
      : R.pluck('type', filteredOrderFilters)

  const computedInventoryCategoryIds = hasSelectedInventoryCategories
    ? selectedInventoryCategoryIds
    : hasSelectedCategories
      ? []
      : defaultInventoryCategoryIds

  const onFetchMore = useCallback(() => {
    dispatch(
      fetchMoreForSearch({
        ...(isSearchCatalogFiltersAndFacetsEnabled
          ? {
              addAggregations: true,
              eventId: selectedAppointmentType,
              filters: computedFilters,
              inventoryCategoryIds: computedInventoryCategoryIds,
              listTypes:
                selectedListTypes.length > 0
                  ? selectedListTypes
                  : DEFAULT_ORDER_LIST_TYPES,
              patientRestrictions: selectedPatientRestrictions,
            }
          : {
              addAggregations: undefined,
              eventId: appointmentId,
              applyPatientRestriction: appliedPatientRestrictions,
              filters: R.pluck('type', orderFilters),
              inventoryCategoryIds: undefined,
              listTypes: undefined,
              patientRestrictions: undefined,
            }),
        clientId,
        forShipments: false,
        listType: OrderListType.FULL,
        patientId,
        searchTerm,
        withTasks: true,
        from: items.length,
        to: items.length + Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
      }),
    )
  }, [
    appointmentId,
    clientId,
    appliedPatientRestrictions,
    isSearchCatalogFiltersAndFacetsEnabled,
    items.length,
    orderFilters,
    patientId,
    searchTerm,
    selectedListTypes,
    computedFilters,
    computedInventoryCategoryIds,
  ])

  useEffect(() => {
    if (!isSearchCatalogFiltersAndFacetsEnabled) {
      onSearch(searchTerm)
    }
  }, [appliedPatientRestrictions, isSearchCatalogFiltersAndFacetsEnabled])

  const prevSearchTermLength = useRef<number>(0)
  const prevSearchTermValue = useRef<string>('')

  useEffect(() => {
    const currentLength = searchTerm.length
    const prevLength = prevSearchTermLength.current
    const prevValue = prevSearchTermValue.current
    const isMinLength = currentLength >= Defaults.MIN_SEARCH_LENGTH
    const wasValidSearch = prevLength >= Defaults.MIN_SEARCH_LENGTH
    const isNowBelowMin = currentLength < Defaults.MIN_SEARCH_LENGTH
    const wasBelowMin = prevLength < Defaults.MIN_SEARCH_LENGTH

    if (isSearchCatalogFiltersAndFacetsEnabled) {
      // Check if this is a completely new search (not just character addition)
      const isNewSearch =
        isMinLength &&
        wasValidSearch &&
        !searchTerm.startsWith(prevValue) &&
        !prevValue.startsWith(searchTerm)

      const shouldResetFiltersAndFacets =
        (isMinLength && wasBelowMin) ||
        currentLength === 0 ||
        (isNowBelowMin && wasValidSearch) ||
        isNewSearch

      if (shouldResetFiltersAndFacets) {
        setFacetSelections({})
        setFilterSelections(DEFAULT_CHARGE_SEARCH_FILTERS)
        setSelectedCategories([])
        setSelectedListTypes(DEFAULT_ORDER_LIST_TYPES)
        setSelectedInventoryCategoryIds([])
        setSelectedPatientRestrictions(
          DEFAULT_CHARGE_SEARCH_FILTERS.patient
            .filter((filter) => filter.enabled)
            .map((filter) => filter.value as PatientRestrictionType),
        )
      }

      if (isNowBelowMin && wasValidSearch && clearSearch) {
        clearSearch()
      }
    }

    prevSearchTermLength.current = currentLength
    prevSearchTermValue.current = searchTerm
  }, [searchTerm, clearSearch])

  useEffect(() => {
    if (isSearchCatalogFiltersAndFacetsEnabled && filtersRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { height } = entry.contentRect
          setFiltersHeight(Math.max(height, MIN_HEIGHT))
        })
      })

      resizeObserver.observe(filtersRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }

    return undefined
  }, [isSearchCatalogFiltersAndFacetsEnabled, theme])

  const firstItemWithoutPrice = items.findIndex(
    (item) => !hasPriceOrAggregatedPrice(item),
  )

  const handleFiltersChange = (filters: FilterSectionData) => {
    setFilterSelections(filters)
    // Fix case sensitivity: use 'patient' (lowercase) to match the filter keys
    const enabledPatientFilters =
      filters.patient?.filter((filter) => filter.enabled) || []

    const restrictions: PatientRestrictionType[] = enabledPatientFilters
      .map((filter) => filter.value as PatientRestrictionType)
      .filter((value): value is PatientRestrictionType =>
        Object.values(PatientFilterType).includes(value as PatientFilterType),
      )

    const currentAppointmentTypeFilter = filters.appointment?.find(
      (filter) => filter.id === AppointmentFilterType.CURRENT_APPOINTMENT_TYPE,
    )

    const newSelectedAppointmentType = currentAppointmentTypeFilter?.enabled
      ? appointmentId
      : undefined
    setSelectedAppointmentType(newSelectedAppointmentType)

    setSelectedPatientRestrictions(restrictions)

    dispatch(
      searchOrders({
        ...(isSearchCatalogFiltersAndFacetsEnabled
          ? {
              addAggregations: true,
              eventId: newSelectedAppointmentType,
              filters: computedFilters,
              inventoryCategoryIds: computedInventoryCategoryIds,
              listTypes: selectedListTypes,
              patientRestrictions: restrictions,
            }
          : {
              addAggregations: undefined,
              eventId: appointmentId,
              filters: R.pluck('type', orderFilters),
              inventoryCategoryIds: undefined,
              listTypes: undefined,
              patientRestrictions: undefined,
            }),
        clientId,
        forShipments: false,
        listType: OrderListType.FULL,
        patientId,
        searchTerm,
        withTasks: true,
        isSOAP: true,
        soapId,
        appointmentId,
      }),
    )
  }

  const handleFacetsChange = (updatedFacets: FilterSectionData) => {
    const newListTypes: OrderListType[] = []
    if (updatedFacets.Fulfillment) {
      updatedFacets.Fulfillment.filter((facet) => facet.enabled).forEach(
        (facet) => {
          const mappedValue =
            FulfillmentFacetToBackendValue[
              facet.value as keyof typeof FulfillmentFacetToBackendValue
            ]
          if (mappedValue) {
            newListTypes.push(mappedValue as OrderListType)
          }
        },
      )
    }

    const newCategories: string[] = []
    const newInventoryCategoryIds: string[] = []
    if (updatedFacets.Categories) {
      updatedFacets.Categories.filter((facet) => facet.enabled).forEach(
        (facet) => {
          const orderTypeValue =
            FACET_TO_ORDER_TYPE_MAPPING[
              facet.value as keyof typeof FACET_TO_ORDER_TYPE_MAPPING
            ]
          if (orderTypeValue) {
            newCategories.push(orderTypeValue)
          } else {
            // Normalize the facet value to handle name mismatches (e.g., "Food And Supplements" → "Food & supplements")
            const normalizedName =
              INVENTORY_CATEGORY_NAME_MAPPING[facet.value] || facet.value
            const categoryId = Utils.findConstantIdByName(
              normalizedName,
              InventoryCategory,
            )
            if (categoryId) {
              newInventoryCategoryIds.push(categoryId)
            }
          }
        },
      )
    }

    setSelectedListTypes(
      newListTypes.length > 0 ? newListTypes : DEFAULT_ORDER_LIST_TYPES,
    )

    setSelectedInventoryCategoryIds(newInventoryCategoryIds)
    setSelectedCategories(newCategories)

    setFacetSelections(updatedFacets)

    // Compute filter values for this function scope to avoid repetition
    const hasNewCategories = newCategories.length > 0
    const hasNewInventoryCategories = newInventoryCategoryIds.length > 0

    const newFilters = hasNewCategories
      ? newCategories
      : hasNewInventoryCategories
        ? []
        : R.pluck('type', filteredOrderFilters)

    const newInventoryCategoryFilters = hasNewInventoryCategories
      ? newInventoryCategoryIds
      : hasNewCategories
        ? []
        : defaultInventoryCategoryIds

    // Get the current appointment filter state to determine eventId
    const currentAppointmentTypeFilter = filterSelections.appointment?.find(
      (filter) => filter.id === AppointmentFilterType.CURRENT_APPOINTMENT_TYPE,
    )
    const currentEventId = currentAppointmentTypeFilter?.enabled
      ? appointmentId
      : undefined

    dispatch(
      searchOrders({
        ...(isSearchCatalogFiltersAndFacetsEnabled
          ? {
              addAggregations: true,
              eventId: currentEventId,
              filters: newFilters,
              inventoryCategoryIds: newInventoryCategoryFilters,
              listTypes:
                newListTypes.length > 0
                  ? newListTypes
                  : DEFAULT_ORDER_LIST_TYPES,
              patientRestrictions: selectedPatientRestrictions,
            }
          : {
              addAggregations: undefined,
              eventId: appointmentId,
              filters: R.pluck('type', orderFilters),
              inventoryCategoryIds: undefined,
              listTypes: undefined,
              patientRestrictions: undefined,
            }),
        clientId,
        forShipments: false,
        listType: OrderListType.FULL,
        patientId,
        searchTerm,
        withTasks: true,
        isSOAP: true,
        soapId,
        appointmentId,
      }),
    )
  }

  return (
    <Grid
      container
      item
      className={classes.mainWrapper}
      height={
        R.isEmpty(items)
          ? isSearchCatalogFiltersAndFacetsEnabled
            ? 'auto'
            : DEFAULT_HEIGHT
          : MIN_HEIGHT
      }
      position="absolute"
      ref={containerRef}
      width="100%"
      wrap="nowrap"
      zIndex={0}
    >
      <Box
        className={classNames(classes.listContainer, {
          [classes.listContainerLegacySpacing]:
            !isSearchCatalogFiltersAndFacetsEnabled,
          [classes.hide]: hasMinSearchLength,
        })}
      >
        <Box display="flex" height="100%">
          {isSearchCatalogFiltersAndFacetsEnabled && (
            <Box className={classes.filterPanel} ref={filtersRef}>
              <FiltersAndFacetsWidget
                facets={facets}
                filters={filterSelections}
                resultsCount={
                  searchResultsTotalCount ?? ordersTotalCount ?? items.length
                }
                onFacetsChange={handleFacetsChange}
                onFiltersChange={handleFiltersChange}
              />
            </Box>
          )}
          <Box className={classes.contentPanel}>
            <Box
              id="scrollableDiv"
              ref={infiniteScrollRef}
              sx={{
                height: isSearchCatalogFiltersAndFacetsEnabled
                  ? filtersHeight
                  : dynamicHeight,
                overflowY: 'auto',
              }}
            >
              <InfiniteScroll
                className={classNames({
                  [classes.hide]: hasMinSearchLength,
                })}
                dataLength={items.length}
                hasMore={hasMoreItems}
                loader={
                  isReceivingOrders && (
                    <CircularProgress
                      color="secondary"
                      size={16}
                      sx={{ ml: 2 }}
                    />
                  )
                }
                next={onFetchMore}
                scrollableTarget="scrollableDiv"
                style={{
                  paddingTop: containerPaddingTop,
                }}
              >
                {isReceivingOrders && (
                  <Grid
                    container
                    justifyContent="center"
                    position="absolute"
                    top="40%"
                  >
                    <CircularProgress color="secondary" size={24} />
                  </Grid>
                )}
                {hasSearchedWithoutResults && (
                  <Text px={2} variant="body">
                    {t('Search:NO_SEARCH_RESULTS')} &apos;
                    <Text strong component="span">
                      {searchTerm}
                    </Text>
                    &apos;
                    {appliedPatientRestrictions && (
                      <>
                        <br />
                        {t('Search:TRY_TURNING_OFF_FILTERS')}
                      </>
                    )}
                  </Text>
                )}
                {!R.isEmpty(items) &&
                  items.map((order, index) => {
                    const searchResultFilter = searchResults.find(
                      (r) => r.item === order,
                    )?.filter
                    const isFirstItem = index === 0
                    const isFirstOccurrenceWithoutPrice =
                      index === firstItemWithoutPrice
                    return (
                      <ChargeSearchResultItem
                        clientId={clientId}
                        contentRef={itemsRefs[index]}
                        disabled={disabled}
                        disabledWeakMap={disabledWeakMap}
                        handleEditListItem={handleEditListItem}
                        handleRefill={handleRefill}
                        handleSavePrescription={handleSavePrescription}
                        isFirstItem={isFirstItem}
                        isFirstOccurrenceWithoutPrice={
                          isFirstOccurrenceWithoutPrice
                        }
                        isReceivingOrders={isReceivingOrders}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        logItemStateGetter={logItemStateGetter}
                        openPrescriptionDialog={openPrescriptionDialog}
                        order={order}
                        patientId={patientId}
                        searchResultFilter={searchResultFilter}
                        searchTerm={searchTerm}
                        onSelect={(itemId, props) => {
                          trackEvent(TrackingEvent.SEARCH_CHARGE_CLICK_RESULT, {
                            searchTerm,
                            selectedItem: order.name,
                            searchPosition: index,
                          })
                          onSelect(itemId, { searchPosition: index, ...props })
                        }}
                      />
                    )
                  })}
              </InfiniteScroll>
            </Box>
          </Box>
        </Box>
      </Box>
    </Grid>
  )
}

export default ChargeSearchResults
